import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Search, User, Menu, X, MessageCircle, Instagram, Mail, MapPin, ChevronRight } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { formatINR } from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import WelcomePopup from "@/components/WelcomePopup";

const CATS = [
  { slug: "vegan-pu-leather", name: "Vegan PU Leather" },
  { slug: "vegan-pu-fur-leather", name: "Vegan PU Fur Leather" },
  { slug: "suede", name: "Suede" },
  { slug: "corduroy", name: "Corduroy" },
  { slug: "cotton", name: "Cotton" },
  { slug: "puffer", name: "Puffer" },
  { slug: "polyester", name: "Polyester" },
  { slug: "windcheater", name: "Windcheater" },
];

function AnnouncementBar() {
  return (
    <div className="bg-[#163A2F] text-[#F8F6F0] py-2 text-[11px] sm:text-xs tracking-[0.2em] uppercase text-center font-light grain relative">
      <span className="relative z-10">Get 10% off on your first order · Code <span className="text-[#C9A227] font-medium">NORBU10</span> · Crafted in India</span>
    </div>
  );
}

function Header({ onMenu }) {
  const { totalQty, setDrawerOpen } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 bg-[#F8F6F0]/85 backdrop-blur-xl border-b hairline">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 h-16 md:h-20 flex items-center justify-between">
        <button
          data-testid="mobile-menu-button"
          onClick={onMenu}
          className="lg:hidden text-[#163A2F]"
          aria-label="Menu"
        >
          <Menu className="w-6 h-6" strokeWidth={1.5} />
        </button>

        <nav className="hidden lg:flex items-center gap-8 text-[12px] uppercase tracking-[0.18em] font-medium text-[#163A2F]">
          <NavLink data-testid="nav-home" to="/" end className={({isActive}) => `link-gold ${isActive ? "text-[#C9A227]" : ""}`}>Home</NavLink>
          <NavLink data-testid="nav-categories" to="/categories" className={({isActive}) => `link-gold ${isActive ? "text-[#C9A227]" : ""}`}>Categories</NavLink>
          <NavLink data-testid="nav-about" to="/about" className={({isActive}) => `link-gold ${isActive ? "text-[#C9A227]" : ""}`}>About Us</NavLink>
          <NavLink data-testid="nav-contact" to="/contact" className={({isActive}) => `link-gold ${isActive ? "text-[#C9A227]" : ""}`}>Contact</NavLink>
        </nav>

        <Link to="/" data-testid="brand-logo" className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2">
          <span className="text-[#163A2F] font-light text-xl md:text-2xl tracking-[0.25em]">NORBU</span>
          <span className="hidden sm:block text-[#C9A227] font-light text-[10px] tracking-[0.4em] mt-1">COLLECTION</span>
        </Link>

        <div className="flex items-center gap-4 md:gap-5 text-[#163A2F]">
          <button data-testid="search-button" aria-label="Search" onClick={() => navigate("/shop")} className="hidden sm:block">
            <Search className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <button data-testid="account-button" aria-label="Account" onClick={() => navigate(user ? "/account" : "/login")}>
            <User className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <button data-testid="cart-button" aria-label="Cart" onClick={() => setDrawerOpen(true)} className="relative">
            <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
            {totalQty > 0 && (
              <span data-testid="cart-count" className="absolute -top-1.5 -right-2 bg-[#C9A227] text-white text-[10px] font-medium w-4 h-4 flex items-center justify-center rounded-full">
                {totalQty}
              </span>
            )}
          </button>
        </div>
      </div>
    </header>
  );
}

function MobileMenu({ open, onClose }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/40 lg:hidden"
          onClick={onClose}
        >
          <motion.div
            initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="absolute left-0 top-0 h-full w-[82%] max-w-sm bg-[#F8F6F0] p-6 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="mobile-menu-panel"
          >
            <div className="flex justify-between items-center mb-8">
              <span className="text-[#163A2F] font-light tracking-[0.25em]">NORBU</span>
              <button onClick={onClose} aria-label="Close" data-testid="close-mobile-menu"><X className="w-5 h-5 text-[#163A2F]" /></button>
            </div>
            <div className="overline text-[#C9A227] mb-3">Menu</div>
            <div className="space-y-1">
              {[
                { to: "/", name: "Home" },
                { to: "/categories", name: "Categories" },
                { to: "/about", name: "About Us" },
                { to: "/contact", name: "Contact" },
              ].map(item => (
                <Link key={item.to} to={item.to} onClick={onClose}
                  className="flex items-center justify-between py-3 border-b hairline text-[#163A2F]">
                  {item.name} <ChevronRight className="w-4 h-4" />
                </Link>
              ))}
            </div>
            <div className="overline text-[#C9A227] mt-8 mb-3">Shop by category</div>
            <div className="space-y-1">
              {CATS.map(c => (
                <Link key={c.slug} to={`/category/${c.slug}`} onClick={onClose}
                  className="flex items-center justify-between py-3 border-b hairline text-[#163A2F] text-sm">
                  {c.name} <ChevronRight className="w-4 h-4" />
                </Link>
              ))}
            </div>
            <div className="mt-10 space-y-2 text-sm text-[#163A2F]">
              <Link to="/account" onClick={onClose} className="block py-2">Account</Link>
              <Link to="/login" onClick={onClose} className="block py-2">Sign in</Link>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function CartDrawer() {
  const { items, drawerOpen, setDrawerOpen, updateQty, removeItem, subtotal } = useCart();
  const navigate = useNavigate();
  return (
    <AnimatePresence>
      {drawerOpen && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-black/40"
          onClick={() => setDrawerOpen(false)}
        >
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="absolute right-0 top-0 h-full w-full sm:max-w-md bg-[#F8F6F0] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="cart-drawer"
          >
            <div className="p-6 flex items-center justify-between border-b hairline">
              <div>
                <div className="overline text-[#C9A227]">Your bag</div>
                <div className="text-[#163A2F] text-lg font-light">{items.length} items</div>
              </div>
              <button onClick={() => setDrawerOpen(false)} data-testid="cart-drawer-close"><X className="w-5 h-5 text-[#163A2F]" /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {items.length === 0 && (
                <div className="text-center py-20">
                  <ShoppingBag className="w-10 h-10 mx-auto text-[#C9A227]" strokeWidth={1} />
                  <p className="mt-4 text-[#163A2F]/70 font-light">Your bag is empty.</p>
                </div>
              )}
              {items.map((it) => {
                const key = `${it.product_id}-${it.size}-${it.color}`;
                return (
                  <div key={key} className="flex gap-4" data-testid={`cart-item-${it.product_id}`}>
                    <img src={it.image} alt={it.name} className="w-20 h-24 object-cover bg-[#163A2F]/5" />
                    <div className="flex-1 text-sm text-[#163A2F]">
                      <div className="flex justify-between gap-2">
                        <div className="font-medium leading-snug">{it.name}</div>
                        <button onClick={() => removeItem(key)} className="text-[#163A2F]/50 hover:text-[#163A2F]" aria-label="Remove"><X className="w-4 h-4" /></button>
                      </div>
                      <div className="text-xs text-[#163A2F]/60 mt-1">{it.size} · {it.color || "Default"}</div>
                      <div className="flex items-center justify-between mt-3">
                        <div className="inline-flex items-center border hairline">
                          <button onClick={() => updateQty(key, it.quantity - 1)} className="px-3 py-1">−</button>
                          <span className="px-3 py-1 text-sm">{it.quantity}</span>
                          <button onClick={() => updateQty(key, it.quantity + 1)} className="px-3 py-1">+</button>
                        </div>
                        <div className="font-medium">{formatINR(it.price * it.quantity)}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t hairline p-6 space-y-4">
              <div className="flex justify-between text-[#163A2F]">
                <span className="overline">Subtotal</span>
                <span className="font-medium">{formatINR(subtotal)}</span>
              </div>
              <button
                disabled={items.length === 0}
                onClick={() => { setDrawerOpen(false); navigate("/checkout"); }}
                data-testid="checkout-button"
                className="w-full bg-[#163A2F] text-white py-4 uppercase text-xs tracking-[0.2em] hover:bg-[#0A1C16] transition-colors disabled:opacity-40"
              >
                Checkout
              </button>
              <button onClick={() => { setDrawerOpen(false); navigate("/cart"); }} className="w-full border border-[#163A2F] text-[#163A2F] py-3 uppercase text-xs tracking-[0.2em]">View bag</button>
            </div>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function WhatsAppButton() {
  return (
    <a
      data-testid="whatsapp-button"
      href="https://wa.me/919560995329?text=Hi%20Norbu%20Collection"
      target="_blank" rel="noreferrer"
      className="fixed bottom-6 right-6 z-[100] w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-lg animate-whats-pulse hover:scale-105 transition-transform"
      aria-label="Chat on WhatsApp"
    >
      <MessageCircle className="w-6 h-6" strokeWidth={1.8} />
    </a>
  );
}

function Footer() {
  return (
    <footer className="bg-[#163A2F] text-[#F8F6F0] grain relative">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 py-20 relative z-10">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-3xl font-light tracking-[0.2em]">NORBU</div>
            <div className="overline text-[#C9A227] mt-1">Collection</div>
            <p className="mt-6 text-[#F8F6F0]/70 font-light leading-relaxed max-w-md">
              Premium menswear jackets crafted in India. Considered fabrications, refined silhouettes, made for the contemporary gentleman.
            </p>
            <div className="mt-6 gold-rule w-24" />
            <div className="mt-6 flex items-center gap-4">
              <a href="https://www.instagram.com/norbu_collection/" target="_blank" rel="noreferrer" aria-label="Instagram" className="hover:text-[#C9A227] transition-colors"><Instagram className="w-5 h-5" /></a>
              <a href="mailto:support.norbucollection@gmail.com" aria-label="Email"><Mail className="w-5 h-5" /></a>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="overline text-[#C9A227] mb-5">Explore</div>
            <ul className="space-y-3 font-light text-[#F8F6F0]/80">
              <li><Link to="/" className="hover:text-[#C9A227]">Home</Link></li>
              <li><Link to="/categories" className="hover:text-[#C9A227]">Categories</Link></li>
              <li><Link to="/about" className="hover:text-[#C9A227]">About Us</Link></li>
              <li><Link to="/contact" className="hover:text-[#C9A227]">Contact</Link></li>
              <li><Link to="/account" className="hover:text-[#C9A227]">My Account</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="overline text-[#C9A227] mb-5">Help</div>
            <ul className="space-y-3 font-light text-[#F8F6F0]/80">
              <li><Link to="/account" className="hover:text-[#C9A227]">Order tracking</Link></li>
              <li><Link to="/shipping-policy" className="hover:text-[#C9A227]">Shipping</Link></li>
              <li><Link to="/refund-policy" className="hover:text-[#C9A227]">Returns &amp; Refunds</Link></li>
              <li><Link to="/contact" className="hover:text-[#C9A227]">Size guide</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="overline text-[#C9A227] mb-5">Studio</div>
            <ul className="space-y-3 font-light text-[#F8F6F0]/80">
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-1 text-[#C9A227]" /><span>New Delhi · India</span></li>
              <li><a href="mailto:support.norbucollection@gmail.com" className="hover:text-[#C9A227] break-all">support.norbucollection@gmail.com</a></li>
              <li><a href="tel:+919560995329" className="hover:text-[#C9A227]">+91 95609 95329</a></li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-[#F8F6F0]/15 flex flex-col md:flex-row justify-between gap-3 text-xs text-[#F8F6F0]/60 font-light tracking-wider">
          <div>© {new Date().getFullYear()} Norbu Collection. All rights reserved.</div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Link data-testid="footer-privacy-link" to="/privacy-policy" className="hover:text-[#C9A227]">Privacy</Link>
            <Link data-testid="footer-terms-link" to="/terms" className="hover:text-[#C9A227]">Terms</Link>
            <Link data-testid="footer-refund-link" to="/refund-policy" className="hover:text-[#C9A227]">Refund &amp; Returns</Link>
            <Link data-testid="footer-shipping-link" to="/shipping-policy" className="hover:text-[#C9A227]">Shipping</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children, hideFooter = false }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-[#F8F6F0]">
      <AnnouncementBar />
      <Header onMenu={() => setMobileOpen(true)} />
      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />
      <main className="flex-1">{children}</main>
      <CartDrawer />
      <WhatsAppButton />
      <WelcomePopup />
      {!hideFooter && <Footer />}
    </div>
  );
}
